import React, { useState } from 'react';
import { EllipsisHorizontalIcon } from '@heroicons/react/24/outline';
import { LuListFilter } from "react-icons/lu";
import ActiveUserAvatars from './ActiveUserAvatars';
import ChatTags from './ChatTags';
import { truncateText, formatTimeAgo, getUserColor, getDarkerTextColor, getMemberInitials, getTagIcon } from '../utils/helpers';

// Function to get proxied image URL for expired S3 URLs
const getProxiedImageUrl = (s3Url) => {
  if (!s3Url) return null;
  
  // If it's already not an S3 URL, just return it as is
  if (!s3Url.includes('amazonaws.com')) {
    return s3Url;
  }
  
  // Extract file key from the S3 URL (everything after the bucket name)
  const urlParts = s3Url.split('/');
  const fileKey = urlParts.slice(3).join('/');
  
  // Return URL that will be handled by our backend proxy endpoint
  return `${process.env.REACT_APP_API_URL}/brand/image-proxy?key=${encodeURIComponent(fileKey)}`;
};

const ChatList = ({
  filteredChats,
  selectedTagFilter,
  projectTags,
  clearFilter,
  handleFilterMenuClick,
  user,
  typingUsers,
  handleChatClick,
  editingChatId,
  editValue,
  setEditValue,
  handleRenameSubmit,
  setEditingChatId,
  handleMenuClick,
  removeTagFromChat
}) => {
  // State to track image errors for each creator
  const [imageErrors, setImageErrors] = useState({});
  // Separate live chats (with active users) from inactive chats
  const liveChats = filteredChats.filter(chat => chat.activeUsers && chat.activeUsers.length > 0);
  const inactiveChats = filteredChats.filter(chat => !chat.activeUsers || chat.activeUsers.length === 0);

  const renderChatRow = (chat, isLive = false) => {
    const creatorName = chat.createdBy?.name || 'Unknown';
    const nameParts = creatorName.split(' ');
    // Check if current user is the creator
    const isCurrentUserCreator = user?.id === chat.createdBy?.id;
    const displayName = isCurrentUserCreator ? 'Me' : (nameParts.length > 1 
      ? `${nameParts[0]} ${nameParts[1].charAt(0)}.`
      : nameParts[0]);
    
    const creatorInitials = getMemberInitials(chat.createdBy);
    const userColor = getUserColor(chat.createdBy?.id || 'unknown');
    const hasProfileImage = chat.createdBy?.profileImage?.url;
    
    return (
      <div
        key={chat._id}
        className={`flex items-center px-4 ${isLive ? 'py-3' : 'py-2.5'} hover:bg-[#EDEDED] dark:hover:bg-[#181818] rounded-2xl cursor-pointer transition-colors group relative`}
      >
        <div className="flex-1 mr-5" onClick={() => handleChatClick(chat._id)}>
          <div className="flex items-start gap-2">
            <div className="flex-1">
              {editingChatId === chat._id ? (
                <input
                  type="text"
                  value={editValue}
                  onChange={(e) => setEditValue(e.target.value)}
                  onBlur={() => handleRenameSubmit(chat._id)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handleRenameSubmit(chat._id);
                    }
                    if (e.key === 'Escape') {
                      setEditingChatId(null);
                      setEditValue('');
                    }
                  }}
                  className="bg-transparent w-full focus:outline-none font-medium text-[14px] text-[#181818] dark:text-[#EDEDED]"
                  autoFocus
                  onClick={(e) => e.stopPropagation()}
                />
              ) : (
                <div>
                  <div className="flex items-center gap-1">
                    <h4 className="font-medium text-[14px] text-[#181818] dark:text-[#EDEDED]">
                      {chat.title}
                    </h4>
                    <ChatTags 
                      tags={chat.tags} 
                      onRemove={(tagId) => removeTagFromChat(chat._id, tagId)}
                    />
                    {/* Three dots menu button */}
                    <button
                      onClick={(e) => handleMenuClick(e, chat._id)}
                      className="opacity-0 group-hover:opacity-100 transition-opacity text-[#999999] dark:text-[#999999] hover:text-[#181818] dark:hover:text-[#EDEDED] p-1 hover:bg-[#EDEDED] dark:hover:bg-[#161616] rounded-lg flex-shrink-0"
                    >
                      <EllipsisHorizontalIcon className="h-4 w-4" />
                    </button>
                  </div>
                  {chat.lastMessage && (
                    <div className="text-xs text-[#999999] dark:text-[#999999] font-normal mt-0.5 line-clamp-1">
                      {truncateText(chat.lastMessage, 80)}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="w-48 flex items-center gap-2 text-sm text-gray-500 dark:text-[#555555] justify-start" onClick={() => handleChatClick(chat._id)}>
          <div className="w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-medium overflow-hidden relative">
            {hasProfileImage && imageErrors[chat.createdBy.id] !== 'failed' ? (
              // Profile picture with colored outline
              <div className="w-full h-full rounded-full overflow-hidden relative">
                {/* Colored border background */}
                <div 
                  className="absolute inset-0 rounded-full"
                  style={{ backgroundColor: userColor.hex }}
                />
                {/* Profile image with slight inset to show the colored border */}
                <div className="absolute inset-[2px] rounded-full overflow-hidden bg-white dark:bg-[#161616]">
                  <img 
                    src={imageErrors[chat.createdBy.id] ? getProxiedImageUrl(chat.createdBy.profileImage.url) : chat.createdBy.profileImage.url} 
                    alt={creatorName}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      if (!imageErrors[chat.createdBy.id]) {
                        // First error - try the proxy URL
                        console.log('S3 URL failed, trying proxy for creator:', creatorName);
                        setImageErrors(prev => ({ ...prev, [chat.createdBy.id]: true }));
                      } else {
                        // Proxy also failed - mark as failed to show monogram fallback
                        console.error('Both S3 URL and proxy failed for creator:', creatorName);
                        setImageErrors(prev => ({ ...prev, [chat.createdBy.id]: 'failed' }));
                      }
                    }}
                    onLoad={() => {
                      if (imageErrors[chat.createdBy.id]) {
                        console.log('Successfully loaded creator image via proxy:', creatorName);
                      }
                    }}
                  />
                </div>
              </div>
            ) : (
              // Monogram with colored background
              <div 
                className={`w-full h-full flex items-center justify-center rounded-full`}
                style={{ 
                  backgroundColor: userColor.hex,
                  color: getDarkerTextColor(userColor.hex)
                }}
              >
                {creatorInitials}
              </div>
            )}
          </div>
          <span className="font-medium text-[#181818] dark:text-[#EDEDED]">
            {displayName}
          </span>
        </div>
        <div className="w-32 flex items-center justify-end" onClick={() => handleChatClick(chat._id)}>
          {isLive ? (
            <ActiveUserAvatars 
              activeUsers={chat.activeUsers} 
              chatId={chat._id}
              typingUsers={typingUsers}
              maxVisible={6} 
            />
          ) : (
            <div className="text-sm text-[#999999] dark:text-[#999999]">
              {formatTimeAgo(chat.lastMessageAt || chat.createdAt)}
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderSectionHeader = (title, isFiltered, showFilter = false) => (
    <div className="flex items-center px-4 mb-2">
      <div className="flex items-center gap-2 flex-1">
        <h3 className="text-sm font-medium text-[#999999] dark:text-[#555555]">
          {title}
        </h3>
        {isFiltered && (
          <div className="flex items-center gap-1">
            <span className="text-xs text-[#999999] dark:text-[#999999]">filtered by</span>
            {(() => {
              const selectedTag = projectTags.find(tag => tag.id === selectedTagFilter);
              if (selectedTag) {
                const IconComponent = getTagIcon(selectedTag.icon);
                return (
                  <div className="flex items-center gap-1">
                    <div
                      className="w-3 h-3 rounded-full flex items-center justify-center"
                      style={{ backgroundColor: selectedTag.color }}
                    >
                      <IconComponent className="w-1.5 h-1.5 text-white" />
                    </div>
                    <span className="text-xs font-medium" style={{ color: selectedTag.color }}>
                      {selectedTag.name}
                    </span>
                    <button
                      onClick={clearFilter}
                      className="text-xs text-[#999999] hover:text-[#181818] dark:hover:text-[#EDEDED] ml-1"
                    >
                      ×
                    </button>
                  </div>
                );
              }
              return null;
            })()}
          </div>
        )}
        {showFilter && (
          <button
            onClick={handleFilterMenuClick}
            className="p-1 text-[#999999] hover:text-[#181818] dark:text-[#999999] dark:hover:text-[#EDEDED] rounded transition-colors"
            title="Filter by tag"
          >
            <LuListFilter className="w-4 h-4" />
          </button>
        )}
      </div>
      <div className="w-48 text-sm font-medium text-[#999999] dark:text-[#555555] text-left">
        Created by
      </div>
      <div className="w-32 text-sm font-medium text-[#999999] dark:text-[#555555] text-right">
        {title === 'Live Chats' ? "Who's here" : 'Interacted'}
      </div>
    </div>
  );

  if (filteredChats.length === 0) {
    return (
      <div className="text-center py-12 text-[#999999] dark:text-[#999999]">
        {selectedTagFilter ? (
          <div>
            <p className="text-lg font-medium mb-2">No chats found with selected tag</p>
            <button
              onClick={clearFilter}
              className="text-sm text-blue-600 dark:text-blue-400 hover:underline"
            >
              Clear filter to see all chats
            </button>
          </div>
        ) : (
          <div>
            <p className="text-lg font-medium mb-2">No Chats Yet</p>
            <p className="text-sm">Start collaborating by creating your first chat</p>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      {/* Live Chats - Non-scrollable */}
      {liveChats.length > 0 && (
        <div className="flex-shrink-0">
          {renderSectionHeader('Live Chats', !!selectedTagFilter, true)}
          <div className="space-y-1">
            {liveChats.map((chat) => renderChatRow(chat, true))}
          </div>
        </div>
      )}

      {/* Conditional separator and spacing between sections */}
      {liveChats.length > 0 && inactiveChats.length > 0 && (
        <div className="flex-shrink-0 my-6 px-4">
          <div className="border-t border-[#C3C3C3] dark:border-[#444444]"></div>
        </div>
      )}

      {/* More Chats - Scrollable */}
      {inactiveChats.length > 0 && (
        <div className="flex-1 flex flex-col overflow-hidden">
          <div className="flex-shrink-0">
            {renderSectionHeader(
              liveChats.length > 0 ? 'More Chats' : 'All Chats', 
              !!selectedTagFilter, 
              liveChats.length === 0
            )}
          </div>
          <div className="flex-1 overflow-y-auto">
            <div className="space-y-1">
              {inactiveChats.map((chat) => renderChatRow(chat, false))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChatList; 